import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Loader2 } from "lucide-react";
import { lazy, Suspense, useEffect } from "react";

import { useBriefingDocumentFileQuery, useBriefingDocumentsQuery } from "@/features/briefings";
import { ApiError } from "@/shared/lib/errors";
import { usePageHeader } from "@/shared/store/page-header";

const PdfViewer = lazy(() =>
  import("@/features/briefings/ui/PdfViewer").then((mod) => ({ default: mod.PdfViewer }))
);

export const Route = createFileRoute("/_authenticated/briefings/documents/$id")({
  component: BriefingDocumentPage,
});

function BriefingDocumentPage() {
  const { id } = Route.useParams();
  const { data: listData } = useBriefingDocumentsQuery();
  const { data: bytes, isLoading, error } = useBriefingDocumentFileQuery(id);
  const document = listData?.documents.find((item) => item.id === id);
  const { setTitle, reset } = usePageHeader();

  useEffect(() => {
    setTitle(document?.title ?? "Document");
    return () => reset();
  }, [document?.title, setTitle, reset]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !bytes || bytes.byteLength === 0) {
    const sessionExpired = error instanceof ApiError && error.status === 401;
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <p className="text-sm text-muted-foreground">
          {sessionExpired ? "Your session has expired. Sign in again." : "Document not found."}
        </p>
        <Link
          to="/briefings"
          className="mt-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Briefings
        </Link>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto">
      <Link
        to="/briefings"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Briefings
      </Link>

      <header className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
          {document?.title ?? "Document"}
        </h1>
        {document ? (
          <p className="mt-1.5 text-sm text-muted-foreground">{document.originalFileName}</p>
        ) : null}
      </header>

      <Suspense
        fallback={
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        }
      >
        <PdfViewer bytes={bytes} />
      </Suspense>
    </div>
  );
}
